import { Box, Text } from '@chakra-ui/react';
import { BerryLoader } from '@web-app/components/BerryLoader';
import { useGetAppUserStatus } from '@web-app/hooks/queries/patient/useGetAppUserStatus';
import { useGetPatientDetails } from '@web-app/hooks/queries/patient/useGetPatientDetails';
import { usePatientJoined } from '@web-app/hooks/usePatientJoined';
import { sanitizeHealthieDate } from '@web-app/utils/date-time.utils';

import { PatientInfoV2UI } from './PatientInfoV2UI';

type Props = {
  clientId: string;
};

/**
 * Connected component that fetches patient data and passes it to PatientInfoV2UI.
 * This component handles data fetching, loading states, and error states.
 * Used in the "Patient" tab of SidePane.
 */
export const PatientInfoV2: React.FC<Props> = ({ clientId }) => {
  const {
    data: patientData,
    isPending: isPatientLoading,
    isError: isPatientError,
  } = useGetPatientDetails(clientId);

  const {
    data: appUserData,
    isPending: isAppUserLoading,
    isError: isAppUserError,
  } = useGetAppUserStatus(clientId);

  usePatientJoined(clientId);

  // Loading state
  if (isPatientLoading) {
    return (
      <Box textAlign="center" py={4}>
        <BerryLoader height="100px" />
      </Box>
    );
  }

  // Error state
  if (isPatientError) {
    return (
      <Text align="center" color="status.error">
        Error loading patient information. Please try again later.
      </Text>
    );
  }

  // No data state
  if (!patientData) {
    return <Text align="center">Patient information unavailable</Text>;
  }

  // Sanitize dates for UI component
  const formattedData = {
    dob: patientData.dob ? sanitizeHealthieDate(patientData.dob) : null,
    height: patientData.height,
    weight: patientData.weight,
    location: patientData.location,
    createdAt: patientData.createdAt
      ? sanitizeHealthieDate(patientData.createdAt)
      : null,
  };

  return (
    <PatientInfoV2UI
      patientData={patientData}
      appUserData={appUserData ?? null}
      isAppUserLoading={isAppUserLoading}
      isAppUserError={isAppUserError}
      formattedData={formattedData}
    />
  );
};

PatientInfoV2.displayName = 'PatientInfoV2';
